/**
 * Internal Notes Component
 * Displays and manages internal notes visible only to staff
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { StickyNote, Plus, Trash2, Clock, User, Lock, ChevronDown, ChevronUp } from 'lucide-react';

export const InternalNotes = ({ 
  notes = [], 
  onAddNote, 
  onDeleteNote,
  currentUser,
  conversationId,
  isLoading = false
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setIsAdding(true);
    try {
      await onAddNote({
        content: newNote.trim(),
        conversationId,
        createdBy: currentUser?.id,
        createdByName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim(),
        createdAt: new Date().toISOString()
      });
      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setIsAdding(false);
    }
  };
  
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="border rounded-lg bg-amber-50/50">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 flex items-center justify-between hover:bg-amber-100/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <StickyNote className="h-4 w-4 text-amber-600" />
          <span className="font-medium text-amber-800">Notes internes</span>
          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
            <Lock className="h-3 w-3 mr-1" />
            Staff uniquement
          </Badge>
          {notes.length > 0 && (
            <Badge className="bg-amber-600 text-white text-xs">
              {notes.length}
            </Badge>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-amber-600" />
        ) : (
          <ChevronDown className="h-4 w-4 text-amber-600" />
        )}
      </button>
      
      {isExpanded && (
        <div className="p-3 pt-0 space-y-3">
          {/* Add Note Form */}
          <div className="flex gap-2">
            <Textarea
              placeholder="Ajouter une note interne (visible uniquement par le staff)..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="min-h-[60px] bg-white border-amber-200 focus:border-amber-400 text-sm"
              rows={2}
            />
            <Button
              size="sm"
              onClick={handleAddNote}
              disabled={!newNote.trim() || isAdding}
              className="bg-amber-600 hover:bg-amber-700 self-end"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Notes List */}
          {notes.length > 0 && (
            <ScrollArea className="max-h-[200px]">
              <div className="space-y-2">
                {notes.map((note, index) => (
                  <div 
                    key={note.id || index}
                    className="p-2 bg-white rounded border border-amber-200 text-sm"
                  >
                    <div className="flex justify-between items-start gap-2">
                      <p className="text-gray-700 flex-1 whitespace-pre-wrap">{note.content}</p>
                      {onDeleteNote && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteNote(note.id)}
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {note.createdByName || 'Staff'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(note.createdAt)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
          
          {notes.length === 0 && (
            <p className="text-xs text-amber-600 text-center py-2">
              Aucune note interne pour cette conversation
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default InternalNotes;
